/**
 * PLAT-10644 — at most ONE translation-editing popover open at a time.
 *
 * The composition quick-edit popover mounts from several independent
 * surfaces (transcript bridge chips, the canvas actions layer) and the
 * phrase editor popover from the translated prose views. Each surface
 * owns its open/close state, so two popovers could sit open side by side
 * (e.g. the same overlay opened from the canvas AND from the transcript).
 *
 * Every popover announces itself on mount via a document-level
 * CustomEvent; any other mounted popover hears an announcement that
 * isn't its own and dismisses itself through its host's close/cancel
 * callback (discarding its draft — same as Cancel). The announcement is
 * dispatched BEFORE the listener registers, so an instance never reacts
 * to its own mount. No host wiring needed.
 */
import { useEffect, useRef } from 'react';

const ANNOUNCE_EVENT = 'arbor:translation-popover-open';

export function useExclusiveTranslationPopover(onDismiss: () => void): void {
  // Latest close callback without re-running the mount-once effect.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const instanceId = Symbol('translation-popover');
    document.dispatchEvent(
      new CustomEvent(ANNOUNCE_EVENT, { detail: instanceId }),
    );
    const handleAnnounce = (e: Event) => {
      if ((e as CustomEvent).detail !== instanceId) onDismissRef.current();
    };
    document.addEventListener(ANNOUNCE_EVENT, handleAnnounce);
    return () => document.removeEventListener(ANNOUNCE_EVENT, handleAnnounce);
  }, []);
}
